import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Play, CheckCircle2, Loader2, ChevronDown, ChevronUp, AlertOctagon, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDistanceStrict, parseISO, format } from "date-fns";

const MILESTONES = [
  { key: "arrived_at",             label: "Arrived at urgent care",  color: "bg-[#E63946] text-white" },
  { key: "triaged_at",             label: "Triage",                   color: "bg-kawaii-peach text-kawaii-text" },
  { key: "seen_by_doctor_at",      label: "Seen by doctor",           color: "bg-kawaii-lavender text-kawaii-text" },
  { key: "antibiotics_started_at", label: "IV antibiotics started",   color: "bg-kawaii-mint text-kawaii-text" },
  { key: "discharged_at",          label: "Discharged / admitted",    color: "bg-white text-kawaii-text border border-kawaii-border" },
];

const RED_FLAGS = [
  { key: "confusion",       label: "New confusion or drowsiness" },
  { key: "mottled_skin",    label: "Mottled or pale/dusky skin" },
  { key: "spo2_low",        label: "SpO₂ below 94% (or breathless)" },
  { key: "hr_high",         label: "Heart rate above 130" },
  { key: "sbp_low",         label: "Systolic BP below 90" },
  { key: "temp_extreme",    label: "Temp above 39 °C or below 36 °C" },
  { key: "rigors",          label: "Uncontrollable shaking / rigors" },
  { key: "no_urine",        label: "Not passing urine (6+ hours)" },
  { key: "spread_visible",  label: "Redness spreading past marked border" },
];

function elapsed(from, to) {
  if (!from || !to) return "—";
  try {
    return formatDistanceStrict(parseISO(from), parseISO(to));
  } catch { return "—"; }
}
function fmt(iso) {
  if (!iso) return "—";
  try { return format(parseISO(iso), "HH:mm"); } catch { return iso; }
}

export default function VisitTracker({ episodeId, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/cellulitis/${episodeId}/visits`);
      // Show the most recent (last in the sorted-asc list)
      setVisit(data.length ? data[data.length - 1] : null);
    } catch { toast.error("Couldn't load visit"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [episodeId]);

  useEffect(() => {
    if (!expanded || !visit || visit.discharged_at) {
      clearInterval(timerRef.current); return;
    }
    timerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timerRef.current);
  }, [expanded, visit]);

  const startVisit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cellulitis/${episodeId}/visits`);
      setVisit(data);
      setExpanded(true);
      toast.success("Visit clock started ⏱");
    } catch { toast.error("Couldn't start"); }
    finally { setBusy(false); }
  };

  const stamp = async (key) => {
    if (!visit) return;
    setBusy(true);
    try {
      const iso = new Date().toISOString();
      const { data } = await api.patch(`/visits/${visit.id}`, { [key]: iso });
      setVisit(data);
      toast.success(`${MILESTONES.find((m) => m.key === key)?.label} logged`);
    } catch { toast.error("Couldn't save"); }
    finally { setBusy(false); }
  };

  const toggleRedFlag = async (flag) => {
    if (!visit) return;
    const current = visit.red_flags || [];
    const next = current.includes(flag) ? current.filter((f) => f !== flag) : [...current, flag];
    try {
      const { data } = await api.patch(`/visits/${visit.id}`, { red_flags: next });
      setVisit(data);
    } catch { toast.error("Couldn't save"); }
  };

  const escalate = () => {
    const flags = (visit?.red_flags || []).map((k) => RED_FLAGS.find((r) => r.key === k)?.label).filter(Boolean);
    const seed = `I'm at urgent care right now with active cellulitis. I've just flagged: ${flags.join(", ")}. This is a possible sepsis picture. What should I do RIGHT NOW to escalate — and what do I say to the nurse to get seen immediately?`;
    window.dispatchEvent(new CustomEvent("open-lymphie", { detail: { seedMessage: seed } }));
  };

  if (loading) return null;

  if (!visit) {
    return (
      <div className="mt-3">
        <button
          onClick={startVisit}
          disabled={busy}
          data-testid={`start-visit-${episodeId}`}
          className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-white border border-[#E63946]/40 text-[#E63946] font-body font-semibold inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />}
          Start urgent-care clock
        </button>
      </div>
    );
  }

  const timerSinceArrival = elapsed(visit.arrived_at, new Date(now).toISOString());

  return (
    <div className="mt-3" data-testid={`visit-tracker-${episodeId}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid={`toggle-visit-${episodeId}`}
        className={`kawaii-btn text-xs px-3 py-1.5 rounded-full font-body font-semibold inline-flex items-center gap-1.5 ${
          visit.discharged_at ? 'bg-white border border-kawaii-mint text-kawaii-text' : 'bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white'
        }`}
      >
        <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
        {visit.discharged_at ? "Visit complete" : `⏱ ${timerSinceArrival} since arrival`}
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mt-3 rounded-2xl bg-white border border-kawaii-border p-4 space-y-2"
          >
            {MILESTONES.map((m, i) => {
              const stamped = visit[m.key];
              const prev = i > 0 ? visit[MILESTONES[i-1].key] : null;
              const gap = i > 0 && stamped && prev ? elapsed(prev, stamped) : null;
              return (
                <div key={m.key} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${stamped ? m.color : 'bg-kawaii-border/50 text-kawaii-textSoft'}`}>
                    {stamped ? <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} /> : <span className="text-xs font-bold">{i+1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-heading font-semibold text-kawaii-text">{m.label}</div>
                    {stamped ? (
                      <div className="text-[11px] text-kawaii-textSoft font-body">
                        {fmt(stamped)}{gap && <span> · <span className="text-kawaii-text font-semibold">+{gap}</span> from previous</span>}
                      </div>
                    ) : (
                      <div className="text-[11px] text-kawaii-textSoft italic font-body">not yet</div>
                    )}
                  </div>
                  {!stamped && (
                    <button
                      onClick={() => stamp(m.key)}
                      disabled={busy}
                      data-testid={`stamp-${m.key}-${episodeId}`}
                      className="kawaii-btn text-[11px] px-2.5 py-1 rounded-full bg-white border border-kawaii-border font-body font-semibold text-kawaii-text disabled:opacity-60"
                    >Log now</button>
                  )}
                </div>
              );
            })}

            {visit.arrived_at && !visit.discharged_at && (
              <div className="mt-3 pt-3 border-t border-kawaii-border grid grid-cols-2 gap-2 text-center">
                <div className="rounded-2xl bg-kawaii-bg/50 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-kawaii-textSoft font-bold">Wait for triage</div>
                  <div className="font-heading text-sm font-semibold text-kawaii-text">
                    {visit.triaged_at ? elapsed(visit.arrived_at, visit.triaged_at) : elapsed(visit.arrived_at, new Date(now).toISOString()) + " ↑"}
                  </div>
                </div>
                <div className="rounded-2xl bg-kawaii-bg/50 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-kawaii-textSoft font-bold">Time to IV antibiotics</div>
                  <div className="font-heading text-sm font-semibold text-kawaii-text">
                    {visit.antibiotics_started_at ? elapsed(visit.arrived_at, visit.antibiotics_started_at) : "waiting"}
                  </div>
                </div>
              </div>
            )}

            {/* Sepsis red-flag checklist */}
            <div className="mt-4 pt-3 border-t border-kawaii-border">
              <div className="flex items-center gap-2 mb-2">
                <AlertOctagon className="w-3.5 h-3.5 text-[#E63946]" strokeWidth={2.5} />
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#E63946] font-bold">Sepsis red flags</span>
                <span className="text-[10px] text-kawaii-textSoft/70 font-body italic">— tap if any appear</span>
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {RED_FLAGS.map((rf) => {
                  const active = (visit.red_flags || []).includes(rf.key);
                  return (
                    <button
                      key={rf.key}
                      type="button"
                      onClick={() => toggleRedFlag(rf.key)}
                      data-testid={`redflag-${rf.key}-${visit.id}`}
                      className={`text-left px-3 py-1.5 rounded-xl text-xs font-body border transition-colors ${
                        active
                          ? 'bg-[#E63946] text-white border-[#E63946] font-semibold'
                          : 'bg-white text-kawaii-text border-kawaii-border hover:bg-[#FFE5E5]/40'
                      }`}
                    >
                      {active ? '⚠️  ' : '○  '}{rf.label}
                    </button>
                  );
                })}
              </div>

              {(visit.red_flags || []).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-3 rounded-2xl bg-[#E63946] text-white p-4"
                  data-testid={`escalate-panel-${visit.id}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertOctagon className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="flex-1">
                      <div className="font-heading text-sm font-semibold leading-tight">ESCALATE NOW</div>
                      <p className="text-xs mt-1 leading-relaxed opacity-95">
                        {(visit.red_flags || []).length} sepsis red flag{visit.red_flags.length > 1 ? 's' : ''} logged. Tell the nearest nurse — this needs a doctor and the sepsis pathway <strong>right now</strong>. Say the word "sepsis" out loud.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={escalate}
                          data-testid={`escalate-lymphie-${visit.id}`}
                          className="kawaii-btn text-[11px] px-3 py-1.5 rounded-full bg-white text-[#E63946] font-body font-semibold inline-flex items-center gap-1"
                        >
                          💬 Ask Lymphie what to say
                        </button>
                        <a
                          href="tel:999"
                          className="kawaii-btn text-[11px] px-3 py-1.5 rounded-full bg-white text-[#E63946] font-body font-semibold inline-flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" strokeWidth={2.5} /> 999
                        </a>
                        <a
                          href="tel:911"
                          className="kawaii-btn text-[11px] px-3 py-1.5 rounded-full bg-white text-[#E63946] font-body font-semibold inline-flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" strokeWidth={2.5} /> 911
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
